// 2026-02 · Sección Admin: Progresión de Entrenamiento del usuario
// Muestra todo lo registrado en Data Progresión (pesos, reps, series y
// fechas) agrupado por grupo muscular con filtro desplegable.
// Montado tanto en ClientProfileModal (Formularios) como en OfflineGenerator
// (Gestor Offline, pestaña de Rutina) para que la admin lo tenga a mano
// mientras arma el plan de la clienta.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}`;

export default function WorkoutProgressionSection({ email }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeMuscle, setActiveMuscle] = useState('__all__');
  const [expanded, setExpanded] = useState(new Set());
  const [refreshToken, setRefreshToken] = useState(0);
  // 2026-02 · Colapsado por defecto (acordeón). Solo carga los datos al abrir.
  const [sectionOpen, setSectionOpen] = useState(false);
  // Formulario para registrar una sesión a nombre de la clienta
  const [showAddSession, setShowAddSession] = useState(false);
  const [newSession, setNewSession] = useState({
    workout_name: '',
    exercise_name: '',
    exercise_category: 'otro',
    sets: [{ weight: '', reps: '', is_warmup: false, is_failure: false }],
  });
  const [savingSession, setSavingSession] = useState(false);

  useEffect(() => {
    if (!email || !sectionOpen) {
      if (!email) setData(null);
      setLoading(false);
      return;
    }
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/api/admin/users/${encodeURIComponent(email)}/workout-progression`);
        if (alive) setData(data);
      } catch (e) {
        if (alive) toast.error('No se pudo cargar la progresión: ' + (e?.response?.data?.detail || e.message));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [email, refreshToken, sectionOpen]);

  const toggle = (name) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const addSetRow = () => {
    setNewSession(prev => ({ ...prev, sets: [...prev.sets, { weight: '', reps: '', is_warmup: false, is_failure: false }] }));
  };
  const removeSetRow = (idx) => {
    setNewSession(prev => ({ ...prev, sets: prev.sets.length > 1 ? prev.sets.filter((_, i) => i !== idx) : prev.sets }));
  };
  const updateSet = (idx, field, value) => {
    setNewSession(prev => ({
      ...prev,
      sets: prev.sets.map((s, i) => i === idx ? { ...s, [field]: value } : s),
    }));
  };

  const submitSession = async () => {
    if (!newSession.exercise_name.trim()) { toast.error('Falta el nombre del ejercicio'); return; }
    const cleanSets = newSession.sets
      .map(s => ({
        weight: Number(s.weight) || 0,
        reps: parseInt(s.reps) || 0,
        is_warmup: !!s.is_warmup,
        is_failure: !!s.is_failure,
      }))
      .filter(s => s.weight > 0 || s.reps > 0);
    if (cleanSets.length === 0) { toast.error('Agrega al menos una serie con peso o repeticiones'); return; }
    setSavingSession(true);
    try {
      const body = {
        workout_name: newSession.workout_name || `${newSession.exercise_name} · registrada por admin`,
        exercises: [{
          exercise_name: newSession.exercise_name.trim(),
          exercise_category: newSession.exercise_category || 'otro',
          sets: cleanSets,
        }],
      };
      const { data: r } = await axios.post(`${API}/api/admin/users/${encodeURIComponent(email)}/workout-progression/session`, body);
      toast.success(`Sesión registrada (${r.exercises_count} ejercicio${r.exercises_count !== 1 ? 's' : ''})`);
      setShowAddSession(false);
      setNewSession({
        workout_name: '', exercise_name: '', exercise_category: 'otro',
        sets: [{ weight: '', reps: '', is_warmup: false, is_failure: false }],
      });
      setRefreshToken(x => x + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar la sesión');
    } finally {
      setSavingSession(false);
    }
  };

  const fmtDate = (iso) => {
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return iso || '';
      return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return iso || ''; }
  };

  const renderSparkline = (sessions) => {
    const vals = sessions.map(s => s.max_weight);
    const max = Math.max(1, ...vals);
    return (
      <div className="flex items-end gap-[2px] h-10" data-testid="progression-sparkline">
        {vals.map((v, i) => {
          const h = Math.max(4, Math.round((v / max) * 40));
          return (
            <div
              key={i}
              className="w-2 bg-cyan-400/70 rounded-sm"
              style={{ height: `${h}px` }}
              title={`${fmtDate(sessions[i].date)}: ${v}kg`}
            />
          );
        })}
      </div>
    );
  };

  if (!email) return null;

  // 2026-02 · Modo colapsado por defecto. Encabezado clickeable para expandir.
  if (!sectionOpen) {
    return (
      <div className="mt-6 border border-cyan-500/30 rounded-lg bg-cyan-500/5" data-testid="workout-progression-section">
        <button
          onClick={() => setSectionOpen(true)}
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-cyan-500/10 transition-colors"
          data-testid="progression-toggle"
        >
          <div className="text-left">
            <p className="text-xs text-cyan-200 uppercase tracking-wider font-semibold">🏋️ Progresión de Entrenamiento</p>
            <p className="text-[11px] text-gray-400 mt-0.5">Clic para expandir el historial de Data Progresión y registrar sesiones</p>
          </div>
          <span className="text-cyan-300 text-lg">▼</span>
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="mt-6 border border-cyan-500/30 rounded-lg p-4 bg-cyan-500/5" data-testid="workout-progression-section">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs text-cyan-200 uppercase tracking-wider font-semibold">🏋️ Progresión de Entrenamiento</p>
          <button onClick={() => setSectionOpen(false)} className="text-cyan-300 text-xs" data-testid="progression-collapse">▲ Cerrar</button>
        </div>
        <p className="text-sm text-gray-400">Cargando…</p>
      </div>
    );
  }
  if (!data || data.total_sessions === 0) {
    return (
      <div className="mt-6 border border-cyan-500/30 rounded-lg p-4 bg-cyan-500/5" data-testid="workout-progression-section">
        <p className="text-xs text-cyan-200 uppercase tracking-wider font-semibold mb-2">🏋️ Progresión de Entrenamiento</p>
        <p className="text-sm text-gray-400 italic mb-3">La clienta aún no registra sesiones en Data Progresión.</p>
        {!showAddSession ? (
          <button
            onClick={() => setShowAddSession(true)}
            className="w-full py-2 bg-cyan-500 hover:bg-cyan-600 text-black font-bold rounded text-sm"
            data-testid="admin-log-session-open"
          >
            ➕ Registrar primera sesión en Data Progresión
          </button>
        ) : (
          <div className="space-y-2 border border-cyan-500/40 rounded-lg bg-cyan-500/10 p-3" data-testid="admin-log-session-panel">
            <div className="flex justify-between items-center">
              <p className="text-xs text-cyan-100 font-semibold">Nueva sesión (se guardará en la cuenta de la clienta)</p>
              <button onClick={() => setShowAddSession(false)} className="text-gray-400 hover:text-white text-xs" data-testid="admin-log-session-cancel">✕</button>
            </div>
            <input
              type="text"
              placeholder="Nombre del ejercicio *"
              value={newSession.exercise_name}
              onChange={(e) => setNewSession(prev => ({ ...prev, exercise_name: e.target.value }))}
              className="w-full bg-gray-900 border border-white/20 text-white rounded px-2 py-1.5 text-sm"
              data-testid="admin-log-session-exercise"
            />
            {newSession.sets.map((s, i) => (
              <div key={i} className="flex items-center gap-1.5" data-testid={`admin-log-session-set-${i}`}>
                <span className="text-[11px] text-gray-400 w-10">Serie {i + 1}</span>
                <input type="number" step="0.5" min="0" placeholder="kg" value={s.weight} onChange={(e) => updateSet(i, 'weight', e.target.value)} className="w-20 bg-gray-900 border border-white/20 text-white rounded px-2 py-1 text-xs" data-testid={`admin-log-session-weight-${i}`} />
                <span className="text-gray-500 text-xs">×</span>
                <input type="number" min="0" placeholder="reps" value={s.reps} onChange={(e) => updateSet(i, 'reps', e.target.value)} className="w-20 bg-gray-900 border border-white/20 text-white rounded px-2 py-1 text-xs" data-testid={`admin-log-session-reps-${i}`} />
                {newSession.sets.length > 1 && <button onClick={() => removeSetRow(i)} className="text-red-400 text-xs">✕</button>}
              </div>
            ))}
            <button onClick={addSetRow} className="text-[11px] text-cyan-300" data-testid="admin-log-session-add-set">+ serie</button>
            <button onClick={submitSession} disabled={savingSession} className="w-full py-2 bg-green-500 hover:bg-green-600 text-black font-bold rounded text-sm disabled:opacity-50" data-testid="admin-log-session-submit">
              {savingSession ? 'Guardando…' : '✓ Guardar en cuenta de la clienta'}
            </button>
          </div>
        )}
      </div>
    );
  }

  const muscleKeys = Object.keys(data.by_muscle || {});
  const visibleMuscles = activeMuscle === '__all__' ? muscleKeys : muscleKeys.filter(k => k === activeMuscle);

  return (
    <div className="mt-6 border border-cyan-500/30 rounded-lg p-4 bg-cyan-500/5" data-testid="workout-progression-section">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <p className="text-xs text-cyan-200 uppercase tracking-wider font-semibold">🏋️ Progresión de Entrenamiento</p>
          <p className="text-[11px] text-gray-400 mt-0.5">
            {data.total_sessions} sesiones · {data.unique_exercises} ejercicios · {data.muscle_groups_count} grupos musculares
            {data.account_created_at && <> · Cuenta desde {fmtDate(data.account_created_at)}</>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setSectionOpen(false)} className="text-cyan-300 text-xs" data-testid="progression-collapse">▲ Cerrar</button>
          <select
            value={activeMuscle}
            onChange={(e) => setActiveMuscle(e.target.value)}
            className="bg-gray-900 border border-cyan-400/40 text-white rounded-md px-3 py-1.5 text-sm"
            data-testid="progression-muscle-filter"
          >
            <option value="__all__">Todos los grupos ({muscleKeys.length})</option>
            {muscleKeys.map(k => (
              <option key={k} value={k}>
                {data.by_muscle[k].label} ({data.by_muscle[k].count})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* 2026-02 · Registrar sesión a nombre de la clienta */}
      <div className="mb-4 border border-cyan-500/40 rounded-lg bg-cyan-500/10 p-3" data-testid="admin-log-session-panel">
        {!showAddSession ? (
          <button
            onClick={() => setShowAddSession(true)}
            className="w-full py-2 bg-cyan-500 hover:bg-cyan-600 text-black font-bold rounded text-sm"
            data-testid="admin-log-session-open"
          >
            ➕ Registrar sesión en Data Progresión
          </button>
        ) : (
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <p className="text-xs text-cyan-100 font-semibold">Nueva sesión (se guardará en la cuenta de la clienta)</p>
              <button onClick={() => setShowAddSession(false)} className="text-gray-400 hover:text-white text-xs" data-testid="admin-log-session-cancel">✕ Cerrar</button>
            </div>
            <input
              type="text"
              placeholder="Nombre del ejercicio (ej. Sentadilla con barra) *"
              value={newSession.exercise_name}
              onChange={(e) => setNewSession(prev => ({ ...prev, exercise_name: e.target.value }))}
              className="w-full bg-gray-900 border border-white/20 text-white rounded px-2 py-1.5 text-sm"
              data-testid="admin-log-session-exercise"
            />
            <div className="grid grid-cols-2 gap-2">
              <select
                value={newSession.exercise_category}
                onChange={(e) => setNewSession(prev => ({ ...prev, exercise_category: e.target.value }))}
                className="bg-gray-900 border border-white/20 text-white rounded px-2 py-1.5 text-sm"
                data-testid="admin-log-session-muscle"
              >
                <option value="otro">Grupo muscular…</option>
                <option value="gluteos">Glúteos</option>
                <option value="cuadriceps">Cuádriceps</option>
                <option value="femoral">Femoral</option>
                <option value="aductores">Aductores</option>
                <option value="abductores">Abductores</option>
                <option value="pantorrilla">Pantorrilla</option>
                <option value="espalda">Espalda</option>
                <option value="pecho">Pecho</option>
                <option value="hombros">Hombros</option>
                <option value="biceps">Bíceps</option>
                <option value="triceps">Tríceps</option>
                <option value="core">Core</option>
                <option value="lumbares">Lumbares</option>
                <option value="antebrazos">Antebrazos</option>
              </select>
              <input
                type="text"
                placeholder="Nombre del workout (opcional)"
                value={newSession.workout_name}
                onChange={(e) => setNewSession(prev => ({ ...prev, workout_name: e.target.value }))}
                className="bg-gray-900 border border-white/20 text-white rounded px-2 py-1.5 text-sm"
                data-testid="admin-log-session-workout-name"
              />
            </div>
            <div className="space-y-1">
              <p className="text-[11px] text-gray-300 font-semibold">Series</p>
              {newSession.sets.map((s, i) => (
                <div key={i} className="flex items-center gap-1.5" data-testid={`admin-log-session-set-${i}`}>
                  <span className="text-[11px] text-gray-400 w-10">Serie {i + 1}</span>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    placeholder="kg"
                    value={s.weight}
                    onChange={(e) => updateSet(i, 'weight', e.target.value)}
                    className="w-20 bg-gray-900 border border-white/20 text-white rounded px-2 py-1 text-xs"
                    data-testid={`admin-log-session-weight-${i}`}
                  />
                  <span className="text-gray-500 text-xs">×</span>
                  <input
                    type="number"
                    min="0"
                    placeholder="reps"
                    value={s.reps}
                    onChange={(e) => updateSet(i, 'reps', e.target.value)}
                    className="w-20 bg-gray-900 border border-white/20 text-white rounded px-2 py-1 text-xs"
                    data-testid={`admin-log-session-reps-${i}`}
                  />
                  <label className="flex items-center gap-1 text-[10px] text-gray-400">
                    <input type="checkbox" checked={s.is_warmup} onChange={(e) => updateSet(i, 'is_warmup', e.target.checked)} />
                    calent.
                  </label>
                  <label className="flex items-center gap-1 text-[10px] text-gray-400">
                    <input type="checkbox" checked={s.is_failure} onChange={(e) => updateSet(i, 'is_failure', e.target.checked)} />
                    fallo
                  </label>
                  {newSession.sets.length > 1 && (
                    <button onClick={() => removeSetRow(i)} className="text-red-400 hover:text-red-200 text-xs px-1" title="Eliminar serie">✕</button>
                  )}
                </div>
              ))}
              <button
                onClick={addSetRow}
                className="text-[11px] text-cyan-300 hover:text-cyan-100"
                data-testid="admin-log-session-add-set"
              >
                + Agregar serie
              </button>
            </div>
            <button
              onClick={submitSession}
              disabled={savingSession}
              className="w-full py-2 bg-green-500 hover:bg-green-600 text-black font-bold rounded text-sm disabled:opacity-50"
              data-testid="admin-log-session-submit"
            >
              {savingSession ? 'Guardando…' : '✓ Guardar sesión en la cuenta de la clienta'}
            </button>
          </div>
        )}
      </div>

      <div className="space-y-4">
        {visibleMuscles.map(mkey => {
          const bucket = data.by_muscle[mkey];
          const exercises = Object.values(bucket.exercises || {});
          return (
            <div key={mkey} className="border border-white/10 rounded-lg bg-black/20 p-3" data-testid={`progression-muscle-${mkey}`}>
              <p className="text-sm font-bold text-cyan-200 mb-2">{bucket.label}</p>
              <div className="space-y-2">
                {exercises.map(ex => {
                  const isOpen = expanded.has(ex.name);
                  const s = ex.summary || {};
                  const stagnant = !!s.is_stagnant;
                  // 2026-02 · Agrupar sesiones por `month_number` para
                  // mostrar la evolución mes a mes desde el alta de la cuenta.
                  const byMonth = {};
                  (ex.sessions || []).forEach(sess => {
                    const m = sess.month_number || 1;
                    (byMonth[m] = byMonth[m] || []).push(sess);
                  });
                  const monthKeys = Object.keys(byMonth).map(Number).sort((a, b) => a - b);
                  return (
                    <div key={ex.name} className={`border rounded ${stagnant ? 'border-red-500/60 bg-red-900/15' : 'border-white/10 bg-gray-900/40'}`}>
                      <button
                        onClick={() => toggle(ex.name)}
                        className={`w-full px-3 py-2 flex justify-between items-center transition-colors ${stagnant ? 'hover:bg-red-500/10' : 'hover:bg-cyan-500/10'}`}
                        data-testid={`progression-exercise-${ex.name.replace(/\s+/g, '_').toLowerCase()}`}
                      >
                        <div className="flex-1 text-left">
                          <p className={`text-sm font-semibold flex items-center gap-2 ${stagnant ? 'text-red-200' : 'text-white'}`}>
                            {stagnant && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/30 text-red-200 font-bold" data-testid={`stagnant-badge-${ex.name.replace(/\s+/g, '_').toLowerCase()}`}>⚠ ESTANCADO</span>}
                            {ex.name}
                          </p>
                          <p className={`text-[11px] ${stagnant ? 'text-red-200/80' : 'text-gray-400'}`}>
                            {s.sessions_count} sesiones · Récord {s.all_time_max_weight}kg × {s.all_time_max_reps} reps · Último: {fmtDate(s.last_date)}
                            {stagnant && ' · Sin progreso en 3+ sesiones'}
                          </p>
                        </div>
                        {ex.sessions.length > 1 && (
                          <div className="hidden sm:block mr-3">{renderSparkline(ex.sessions)}</div>
                        )}
                        <span className={`text-xs ml-2 ${stagnant ? 'text-red-300' : 'text-cyan-300'}`}>{isOpen ? '▲' : '▼'}</span>
                      </button>
                      {isOpen && (
                        <div className="px-3 pb-3 pt-1 space-y-3 border-t border-white/5">
                          {ex.sessions.length > 1 && (
                            <div className="sm:hidden">{renderSparkline(ex.sessions)}</div>
                          )}
                          {/* 2026-02 · Historial agrupado por mes desde el alta */}
                          {monthKeys.map(m => (
                            <div key={m} data-testid={`progression-month-${ex.name.replace(/\s+/g, '_').toLowerCase()}-${m}`}>
                              <p className="text-[11px] uppercase tracking-wider text-cyan-300 font-bold mb-1 border-b border-cyan-500/20 pb-0.5">
                                Mes {m} <span className="text-gray-500 font-normal">· {byMonth[m].length} sesión{byMonth[m].length !== 1 ? 'es' : ''}</span>
                              </p>
                              <div className="overflow-x-auto">
                                <table className="w-full text-xs" data-testid={`progression-table-${ex.name.replace(/\s+/g, '_').toLowerCase()}-${m}`}>
                                  <thead>
                                    <tr className="text-[10px] uppercase text-gray-400 border-b border-white/10">
                                      <th className="text-left py-1 pr-2">Fecha</th>
                                      <th className="text-left py-1 pr-2">Series</th>
                                      <th className="text-right py-1 pr-2">Máx kg</th>
                                      <th className="text-right py-1 pr-2">Máx reps</th>
                                      <th className="text-right py-1">Volumen</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {[...byMonth[m]].reverse().map((sess, i) => (
                                      <tr key={i} className="border-b border-white/5 last:border-0">
                                        <td className="py-1.5 pr-2 text-gray-200 whitespace-nowrap">{fmtDate(sess.date)}</td>
                                        <td className="py-1.5 pr-2 text-gray-300">
                                          {sess.sets.map((st, j) => (
                                            <span key={j} className={`inline-block mr-1.5 px-1.5 py-0.5 rounded text-[10px] ${st.is_warmup ? 'bg-gray-700 text-gray-400' : 'bg-cyan-900/50 text-cyan-100'}`}>
                                              {st.weight}×{st.reps}{st.is_warmup ? '(c)' : ''}{st.is_failure ? '⚡' : ''}
                                            </span>
                                          ))}
                                        </td>
                                        <td className="py-1.5 pr-2 text-right text-cyan-300 font-semibold">{sess.max_weight}</td>
                                        <td className="py-1.5 pr-2 text-right text-cyan-300 font-semibold">{sess.max_reps}</td>
                                        <td className="py-1.5 text-right text-gray-300">{sess.total_volume}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
